import React, { useState } from 'react';
import { Monitor, Smartphone, Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { Button } from '../components/ui/Button';
import { BackLink } from '../components/ui/BackLink';
import { Alert } from '../components/ui/Alert';
import Card from '../components/ui/Card';
import Stack from '../components/ui/Stack';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import NoticeBar from '../components/ui/NoticeBar';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Sparkles, Wallet } from 'lucide-react';

/**
 * Internal STORYBOARD — a living catalog of our design tokens, reusable UI primitives, and public
 * pages, previewable at desktop + mobile widths. For internal review only (noindex + robots-blocked;
 * not in the sitemap). Add a component demo to the COMPONENTS list or a route to PAGE_PREVIEWS.
 */

type Viewport = 'desktop' | 'mobile';
const FRAME: Record<Viewport, number> = { desktop: 1280, mobile: 390 };

// Literal class strings (Tailwind purges dynamic names) — the monochrome scale + semantic accents.
const SECONDARY_SWATCHES = [
  { step: '50', cls: 'bg-secondary-50' }, { step: '100', cls: 'bg-secondary-100' }, { step: '200', cls: 'bg-secondary-200' },
  { step: '300', cls: 'bg-secondary-300' }, { step: '400', cls: 'bg-secondary-400' }, { step: '500', cls: 'bg-secondary-500' },
  { step: '600', cls: 'bg-secondary-600' }, { step: '700', cls: 'bg-secondary-700' }, { step: '800', cls: 'bg-secondary-800' },
  { step: '900', cls: 'bg-secondary-900' }, { step: '950', cls: 'bg-secondary-950' },
];
const SEMANTIC_SWATCHES = [
  { label: 'error', cls: 'bg-error-500' }, { label: 'warning', cls: 'bg-warning-500' },
  { label: 'success', cls: 'bg-success-500' }, { label: 'primary', cls: 'bg-primary-500' },
];

const PAGE_PREVIEWS = [
  { path: '/', label: 'Landing' },
  { path: '/pricing', label: 'Pricing' },
  { path: '/faq', label: 'FAQ' },
  { path: '/tutorials', label: 'Tutorials' },
  { path: '/tutorials/make-a-song', label: 'Tutorial' },
  { path: '/authors/nova-bright', label: 'Author' },
  { path: '/agent/magnus', label: 'Agent profile' },
];

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-12">
    <h2 className="text-sm font-semibold uppercase tracking-wide text-secondary-400 mb-4">{title}</h2>
    {children}
  </section>
);

const Demo: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="rounded-xl border border-secondary-200 dark:border-secondary-800 p-5">
    <p className="text-[11px] uppercase tracking-wide text-secondary-400 mb-3">{label}</p>
    <div className="flex flex-wrap items-center gap-3">{children}</div>
  </div>
);

const Storyboard: React.FC = () => {
  const { effectiveTheme, setThemeMode } = useTheme();
  const [viewport, setViewport] = useState<Viewport>('desktop');
  const [tab, setTab] = useState<'tokens' | 'components' | 'pages'>('components');
  const [confirmOpen, setConfirmOpen] = useState(false);
  useDocumentMeta({ title: 'Storyboard — Wisetree AI (internal)' });

  const ViewToggle = (
    <div className="inline-flex rounded-lg border border-secondary-200 dark:border-secondary-700 overflow-hidden">
      {(['desktop', 'mobile'] as Viewport[]).map((v) => (
        <button key={v} onClick={() => setViewport(v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs ${viewport === v ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}>
          {v === 'desktop' ? <Monitor className="w-3.5 h-3.5" /> : <Smartphone className="w-3.5 h-3.5" />}{v}
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
      {/* Sticky toolbar */}
      <div className="sticky top-0 z-10 border-b border-secondary-200 dark:border-secondary-800 bg-white/90 dark:bg-black/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="font-bold">Storyboard</h1>
            <span className="text-[11px] text-secondary-400">internal · components & pages</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-secondary-200 dark:border-secondary-700 overflow-hidden">
              {(['tokens', 'components', 'pages'] as const).map((t) => (
                <button key={t} onClick={() => setTab(t)}
                  className={`px-3 py-1.5 text-xs capitalize ${tab === t ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}>{t}</button>
              ))}
            </div>
            {tab !== 'tokens' && ViewToggle}
            <button onClick={() => setThemeMode(effectiveTheme === 'dark' ? 'light' : 'dark')}
              className="p-2 rounded-lg text-secondary-400 hover:text-secondary-900 dark:hover:text-white hover:bg-secondary-100 dark:hover:bg-secondary-800" title="Toggle theme">
              {effectiveTheme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* ── TOKENS ── */}
        {tab === 'tokens' && (
          <>
            <Section title="Monochrome scale (secondary)">
              <div className="flex flex-wrap gap-2">
                {SECONDARY_SWATCHES.map((s) => (
                  <div key={s.step} className="text-center">
                    <div className={`w-16 h-16 rounded-lg border border-secondary-200 dark:border-secondary-800 ${s.cls}`} />
                    <p className="mt-1 text-[11px] text-secondary-400">{s.step}</p>
                  </div>
                ))}
              </div>
            </Section>
            <Section title="Semantic (sparingly)">
              <div className="flex flex-wrap gap-4">
                {SEMANTIC_SWATCHES.map((s) => (
                  <div key={s.label} className="text-center">
                    <div className={`w-16 h-16 rounded-lg ${s.cls}`} />
                    <p className="mt-1 text-[11px] text-secondary-400">{s.label}</p>
                  </div>
                ))}
              </div>
            </Section>
            <Section title="Spacing (8pt grid)">
              <div className="space-y-2">
                {[2, 3, 4, 6, 8].map((n) => (
                  <div key={n} className="flex items-center gap-3">
                    <span className="w-10 text-[11px] text-secondary-400 tabular-nums">{n * 4}px</span>
                    <div className="h-3 rounded bg-secondary-900 dark:bg-white" style={{ width: n * 4 * 2 }} />
                  </div>
                ))}
              </div>
            </Section>
          </>
        )}

        {/* ── COMPONENTS ── (framed at the chosen viewport width) */}
        {tab === 'components' && (
          <div className="overflow-x-auto">
            <div className="mx-auto" style={{ width: FRAME[viewport], maxWidth: '100%' }}>
              <Stack gap="md">
                <Demo label="Button — variants">
                  <Button variant="primary">Primary</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="danger">Danger</Button>
                </Demo>
                <Demo label="Button — sizes · icon · loading">
                  <Button size="sm">Small</Button>
                  <Button size="md">Medium</Button>
                  <Button size="lg">Large</Button>
                  <Button icon={<Sparkles className="w-4 h-4" />}>With icon</Button>
                  <Button loading>Loading</Button>
                  <Button disabled>Disabled</Button>
                </Demo>
                <Demo label="BackLink"><BackLink to="#" label="Back to chats" /></Demo>
                <Demo label="Alert — variants">
                  <div className="w-full space-y-2">
                    <Alert variant="error" title="Something went wrong">Couldn’t reach the server. Try again.</Alert>
                    <Alert variant="warning">You’re close to your monthly limit.</Alert>
                    <Alert variant="info">Heads up — this is a preview.</Alert>
                    <Alert variant="success">Saved.</Alert>
                  </div>
                </Demo>
                <Demo label="NoticeBar"><div className="w-full"><NoticeBar icon={<Wallet className="w-4 h-4 text-secondary-500" />}>You’re out of credits — top up to keep creating.</NoticeBar></div></Demo>
                <Demo label="Card — paddings">
                  <Card padding="sm">padding sm</Card>
                  <Card padding="md">padding md</Card>
                  <Card padding="lg">padding lg</Card>
                </Demo>
                <Demo label="LoadingSpinner — sizes">
                  <LoadingSpinner size="xs" /><LoadingSpinner size="sm" /><LoadingSpinner size="md" /><LoadingSpinner size="lg" />
                </Demo>
                <Demo label="ConfirmDialog">
                  <Button variant="secondary" onClick={() => setConfirmOpen(true)}>Open dialog</Button>
                  <ConfirmDialog open={confirmOpen} onOpenChange={setConfirmOpen} title="Archive this chat?"
                    description="It’ll move to your archived list. You can restore it later."
                    confirmText="Archive" onConfirm={() => setConfirmOpen(false)} />
                </Demo>
              </Stack>
            </div>
          </div>
        )}

        {/* ── PAGES ── (iframed at the chosen viewport width) */}
        {tab === 'pages' && (
          <div className="space-y-8">
            {PAGE_PREVIEWS.map((p) => (
              <div key={p.path}>
                <div className="mb-2 flex items-center gap-2 text-sm">
                  <span className="font-medium">{p.label}</span>
                  <a href={p.path} target="_blank" rel="noreferrer" className="text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200 text-xs underline">{p.path} ↗</a>
                </div>
                <div className="overflow-x-auto rounded-xl border border-secondary-200 dark:border-secondary-800">
                  <iframe
                    // Remount on theme change so the iframed page reloads and reads the just-written
                    // theme from (same-origin) localStorage — otherwise the preview keeps its load-time
                    // theme and ignores the toolbar toggle.
                    key={`${p.path}-${effectiveTheme}`}
                    title={p.label}
                    src={p.path}
                    style={{ width: FRAME[viewport], height: 640, border: 'none', display: 'block' }}
                    className="bg-white dark:bg-black"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Storyboard;
